import React, { useState } from 'react';
import { Alert, Input } from 'antd';
import { SettingsModal } from '../../pgProviderRoute/common/SettingsModal';
import { extractErrorMessage } from '../../../lib/utils/checkErrorResponse';
import { FeatureFlagDeleteResponse, FeatureFlagItem } from '../../../lib/api/featureFlag';
import { useDeleteFeatureFlagMutation } from '../hooks/useFeatureFlagMutations';
import { overrideLabel } from '../utils/featureFlagLabels';
import { FLAG_TEXT_MAX_LENGTH } from './FeatureFlagSwitchModal';
import { SwitchModalStyle } from './styles';

type FeatureFlagDeleteModalProps = {
  flag: FeatureFlagItem;
  onDeleted: (response: FeatureFlagDeleteResponse) => void;
  onClose: () => void;
};

/** 코드에 없는 키의 남은 값 지우기 — 서버는 이 값을 읽지 않으니 동작은 바뀌지 않는다 */
export const FeatureFlagDeleteModal = (props: FeatureFlagDeleteModalProps): React.JSX.Element => {
  const { flag, onDeleted, onClose } = props;
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const mutation = useDeleteFeatureFlagMutation();
  const canDelete = reason.trim().length > 0 && !mutation.isLoading;

  const handleDelete = async (): Promise<void> => {
    if (!canDelete) return;
    setError(null);
    try {
      onDeleted(await mutation.mutateAsync({ ticketKey: flag.ticketKey, reason: reason.trim() }));
    } catch (deleteError) {
      setError(
        deleteError instanceof Error ? extractErrorMessage(deleteError) : '지우지 못했습니다. 다시 시도해 주세요.',
      );
    }
  };

  return (
    <SettingsModal
      open
      destroyOnClose
      title={`${flag.ticketKey} 값 지우기`}
      width={560}
      okText="값 지우기"
      cancelText="취소"
      onOk={handleDelete}
      onCancel={onClose}
      confirmLoading={mutation.isLoading}
      okButtonProps={{ disabled: !canDelete }}
      cancelButtonProps={{ disabled: mutation.isLoading }}
      closable={!mutation.isLoading}
      maskClosable={!mutation.isLoading}
      keyboard={!mutation.isLoading}
    >
      <SwitchModalStyle>
        <p>
          코드에 없는 플래그의 남은 값({overrideLabel(flag.dbEnabled)})입니다. 서버는 이 값을 읽지 않으니 지워도 동작이
          바뀌지 않습니다.
        </p>
        {error && <Alert type="error" showIcon message={error} />}
        <div>
          <label className="flag-field-label" htmlFor="feature-flag-delete-reason">
            사유 (필수)
          </label>
          <Input.TextArea
            id="feature-flag-delete-reason"
            rows={2}
            maxLength={FLAG_TEXT_MAX_LENGTH}
            placeholder="예) 코드에서 졸업한 플래그 정리"
            value={reason}
            disabled={mutation.isLoading}
            onChange={(event) => setReason(event.target.value)}
          />
        </div>
      </SwitchModalStyle>
    </SettingsModal>
  );
};
